import React, { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import styled from 'styled-components';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import DataTable from '../ui/DataTable';
import { Chip, SetTabs, SetTab, Tag } from '../ui/primitives';
import {
  fetchDemoFeatures,
  updateDemoFeature,
  bulkUpdateDemoFeatures,
} from '../services/demoPolicyAdminApi';

const TAB_NAV = 'nav';
const TAB_ACTION = 'action';
const TAB_YAKI = 'yaki';
const YAKI_SECTION_KEY = 'nav.yaki';

/** Features grouped under the Yaki admin tab (chatbot + course analysis). */
function isYakiFeature(row) {
  return row?.sectionKey === YAKI_SECTION_KEY || row?.featureKey === YAKI_SECTION_KEY;
}

// Access option sets by feature type. Values are the int codes stored in the DB
// (1 = visible/allow, 0 = hidden, 2 = disabled/read-only).
const NAV_OPTIONS = [
  { value: 1, label: 'Visible' },
  { value: 2, label: 'Disabled' },
  { value: 0, label: 'Hidden' },
];
const ACTION_OPTIONS = [
  { value: 1, label: 'Allow' },
  { value: 2, label: 'Read-only' },
  { value: 0, label: 'Disabled' },
];

const PAGE_SIZE = 50;

const ACTION_SUBGROUPS = [
  {
    key: 'page',
    label: 'Page actions',
    test: (key) =>
      !key.includes('.detail.') &&
      !key.includes('.question.') &&
      !key.includes('.wizard.') &&
      !key.includes('.nav.') &&
      !/\.(save|confirm|aiSave|saveScript)$/.test(key) &&
      !/\.(edit|delete|copy|disable|bulkDelete|toggleStatus|approve|reject)$/.test(key),
  },
  {
    key: 'row',
    label: 'Row menu (⋯)',
    test: (key) =>
      /\.(edit|delete|copy|disable|bulkDelete|toggleStatus|approve|reject|assign|settings|review|resource|analyze|toggleAccess|viewDetail|importCourse|configureImport)$/.test(
        key,
      ) && !key.includes('.detail.') && !key.includes('.question.'),
  },
  {
    key: 'menu',
    label: 'Page menu',
    test: (key) => key.includes('.nav.'),
  },
  {
    key: 'dialog',
    label: 'Dialogs / Save',
    test: (key) =>
      key.includes('.wizard.') ||
      key.includes('.detail.') ||
      key.includes('.question.') ||
      /\.(save|confirm|aiSave|saveScript|publish|upload)$/.test(key),
  },
];

function inferActionSubgroup(featureKey) {
  const key = featureKey || '';
  for (const sg of ACTION_SUBGROUPS) {
    if (sg.test(key)) return sg.label;
  }
  return 'Page actions';
}

function subgroupKeyFor(featureKey) {
  const key = featureKey || '';
  for (const sg of ACTION_SUBGROUPS) {
    if (sg.test(key)) return sg.key;
  }
  return 'page';
}

function sortNavFeatures(list) {
  return [...list].sort((a, b) => {
    const cat = (a.category || '').localeCompare(b.category || '');
    if (cat !== 0) return cat;
    return (a.label || '').localeCompare(b.label || '');
  });
}

function sortActionFeatures(list) {
  return [...list].sort((a, b) => {
    const sec = (a.section || '').localeCompare(b.section || '');
    if (sec !== 0) return sec;
    const sg =
      subgroupKeyFor(a.featureKey).localeCompare(subgroupKeyFor(b.featureKey));
    if (sg !== 0) return sg;
    return (a.label || '').localeCompare(b.label || '');
  });
}

function groupNavByCategory(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const cat = row.category || 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(row);
  });
  return Array.from(map.entries()).map(([category, items]) => ({
    category,
    rows: sortNavFeatures(items),
  }));
}

function groupActionsBySection(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const key = row.sectionKey || row.section || 'other';
    const label = row.section || row.label || key;
    if (!map.has(key)) map.set(key, { key, label, rows: [] });
    map.get(key).rows.push(row);
  });
  return Array.from(map.values())
    .map((section) => {
      const bySubgroup = new Map();
      section.rows.forEach((row) => {
        const sgKey = subgroupKeyFor(row.featureKey);
        const sgDef = ACTION_SUBGROUPS.find((s) => s.key === sgKey) || ACTION_SUBGROUPS[0];
        if (!bySubgroup.has(sgKey)) {
          bySubgroup.set(sgKey, { key: sgKey, label: sgDef.label, rows: [] });
        }
        bySubgroup.get(sgKey).rows.push(row);
      });
      return {
        ...section,
        subgroups: Array.from(bySubgroup.values()).map((sg) => ({
          ...sg,
          rows: sortActionFeatures(sg.rows),
        })),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function appliesToLabel(row) {
  return row.uiLocation || `${row.label} (${row.featureKey})`;
}

const DemoAccessSettings = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_NAV);
  const [savingKey, setSavingKey] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [navSelectedRows, setNavSelectedRows] = useState([]);
  const [actionSelectedRows, setActionSelectedRows] = useState([]);
  const [yakiSelectedRows, setYakiSelectedRows] = useState([]);
  const [navSectionFilter, setNavSectionFilter] = useState('all');
  const [actionSectionFilter, setActionSectionFilter] = useState('all');
  const [yakiSearch, setYakiSearch] = useState('');
  const [navSearch, setNavSearch] = useState('');
  const [actionSearch, setActionSearch] = useState('');
  const [navTableResetKey, setNavTableResetKey] = useState(0);
  const [actionTableResetKey, setActionTableResetKey] = useState(0);
  const [yakiTableResetKey, setYakiTableResetKey] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await fetchDemoFeatures();
        if (active) setFeatures(Array.isArray(data) ? data : []);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load demo features.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const navRows = useMemo(
    () => sortNavFeatures(features.filter((f) => f.type === 'NAV')),
    [features],
  );
  const actionRows = useMemo(
    () => sortActionFeatures(features.filter((f) => f.type === 'ACTION' && !isYakiFeature(f))),
    [features],
  );
  const yakiRows = useMemo(
    () => features.filter(isYakiFeature).sort((a, b) => {
      if (a.type !== b.type) return a.type === 'NAV' ? -1 : 1;
      return (a.label || '').localeCompare(b.label || '');
    }),
    [features],
  );

  const navSectionOptions = useMemo(() => {
    const seen = new Map();
    navRows.forEach((f) => {
      const key = f.category || 'other';
      if (!seen.has(key)) seen.set(key, f.category || key);
    });
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [navRows]);

  const actionSectionOptions = useMemo(() => {
    const seen = new Map();
    actionRows.forEach((f) => {
      const key = f.sectionKey || f.section || 'other';
      if (!seen.has(key)) seen.set(key, f.section || f.label || key);
    });
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [actionRows]);

  const filterBySearch = (rows, term, keys) => {
    const q = term.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      keys.some((k) => {
        const v = r[k];
        return v != null && String(v).toLowerCase().includes(q);
      }),
    );
  };

  const filteredNavRows = useMemo(() => {
    let rows = navRows;
    if (navSectionFilter !== 'all') {
      rows = rows.filter((f) => f.category === navSectionFilter);
    }
    return filterBySearch(rows, navSearch, ['label', 'featureKey', 'category']);
  }, [navRows, navSectionFilter, navSearch]);

  const filteredActionRows = useMemo(() => {
    let rows = actionRows;
    if (actionSectionFilter !== 'all') {
      rows = rows.filter((f) => (f.sectionKey || f.section) === actionSectionFilter);
    }
    return filterBySearch(rows, actionSearch, [
      'label',
      'featureKey',
      'section',
      'uiLocation',
    ]);
  }, [actionRows, actionSectionFilter, actionSearch]);

  const filteredYakiRows = useMemo(
    () => filterBySearch(yakiRows, yakiSearch, [
      'label',
      'featureKey',
      'uiLocation',
      'section',
    ]),
    [yakiRows, yakiSearch],
  );

  const filteredYakiNavRows = useMemo(
    () => filteredYakiRows.filter((f) => f.type === 'NAV'),
    [filteredYakiRows],
  );
  const filteredYakiActionRows = useMemo(
    () => sortActionFeatures(filteredYakiRows.filter((f) => f.type === 'ACTION')),
    [filteredYakiRows],
  );

  const navCategoryGroups = useMemo(
    () => groupNavByCategory(filteredNavRows),
    [filteredNavRows],
  );

  const actionSectionGroups = useMemo(
    () => groupActionsBySection(filteredActionRows),
    [filteredActionRows],
  );

  const selectedRows =
    activeTab === TAB_NAV
      ? navSelectedRows
      : activeTab === TAB_YAKI
        ? yakiSelectedRows
        : actionSelectedRows;
  const isNavTab = activeTab === TAB_NAV;
  const isYakiTab = activeTab === TAB_YAKI;

  const handleChange = async (feature, nextAccess) => {
    const prev = feature.accessCode;
    setSavingKey(feature.featureKey);
    setFeatures((list) =>
      list.map((f) => (f.featureKey === feature.featureKey ? { ...f, accessCode: nextAccess } : f)),
    );
    try {
      await updateDemoFeature(feature.featureKey, nextAccess);
      setToast({ open: true, severity: 'success', message: `${feature.label} updated.` });
    } catch (e) {
      setFeatures((list) =>
        list.map((f) => (f.featureKey === feature.featureKey ? { ...f, accessCode: prev } : f)),
      );
      setToast({ open: true, severity: 'error', message: e.message || 'Update failed.' });
    } finally {
      setSavingKey(null);
    }
  };

  const handleBulkApply = async (accessCode, label) => {
    if (selectedRows.length === 0 || bulkSaving) return;
    const keys = selectedRows.map((r) => r.featureKey);
    const prevByKey = new Map(selectedRows.map((r) => [r.featureKey, r.accessCode]));

    setBulkSaving(true);
    setFeatures((list) =>
      list.map((f) => (keys.includes(f.featureKey) ? { ...f, accessCode } : f)),
    );

    try {
      await bulkUpdateDemoFeatures({
        featureKeys: keys,
        access: accessCode,
      });
      setToast({
        open: true,
        severity: 'success',
        message: `${keys.length} feature${keys.length === 1 ? '' : 's'} set to ${label}.`,
      });
      if (isNavTab) {
        setNavTableResetKey((k) => k + 1);
        setNavSelectedRows([]);
      } else if (isYakiTab) {
        setYakiTableResetKey((k) => k + 1);
        setYakiSelectedRows([]);
      } else {
        setActionTableResetKey((k) => k + 1);
        setActionSelectedRows([]);
      }
    } catch (e) {
      setFeatures((list) =>
        list.map((f) =>
          prevByKey.has(f.featureKey) ? { ...f, accessCode: prevByKey.get(f.featureKey) } : f,
        ),
      );
      setToast({ open: true, severity: 'error', message: e.message || 'Bulk update failed.' });
    } finally {
      setBulkSaving(false);
    }
  };

  const clearSelection = () => {
    if (isNavTab) {
      setNavTableResetKey((k) => k + 1);
      setNavSelectedRows([]);
    } else if (isYakiTab) {
      setYakiTableResetKey((k) => k + 1);
      setYakiSelectedRows([]);
    } else {
      setActionTableResetKey((k) => k + 1);
      setActionSelectedRows([]);
    }
  };

  const handleActionSelectionChange = (sectionKey, subgroupKey, rows) => {
    setActionSelectedRows((prev) => {
      const without = prev.filter(
        (r) =>
          !(
            (r.sectionKey || r.section) === sectionKey &&
            subgroupKeyFor(r.featureKey) === subgroupKey
          ),
      );
      return [...without, ...rows];
    });
  };

  const renderAccessSelect = (feature, options) => {
    const disabled = savingKey === feature.featureKey || bulkSaving;
    return (
      <AccessControl size="small">
        <Select
          value={feature.accessCode}
          disabled={disabled}
          onChange={(e) => handleChange(feature, Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          MenuProps={{ onClick: (e) => e.stopPropagation() }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </AccessControl>
    );
  };

  const navBulkActions = (
    <>
      {bulkSaving && <CircularProgress size={18} />}
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(1, 'visible')}
      >
        Set visible
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(2, 'disabled')}
      >
        Set disabled
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer', color: '#d6455d' }}
        onClick={() => !bulkSaving && handleBulkApply(0, 'hidden')}
      >
        Set hidden
      </Chip>
      <Chip $soft style={{ cursor: 'pointer' }} onClick={clearSelection}>
        Clear selection
      </Chip>
    </>
  );

  const yakiBulkActions = (
    <>
      {bulkSaving && <CircularProgress size={18} />}
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(1, 'allow / visible')}
      >
        Set allow / visible
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(2, 'read-only / disabled')}
      >
        Set read-only / disabled
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer', color: '#d6455d' }}
        onClick={() => !bulkSaving && handleBulkApply(0, 'hidden')}
      >
        Set hidden
      </Chip>
      <Chip $soft style={{ cursor: 'pointer' }} onClick={clearSelection}>
        Clear selection
      </Chip>
    </>
  );

  const renderYakiAccessSelect = (feature) => {
    const options = feature.type === 'NAV' ? NAV_OPTIONS : ACTION_OPTIONS;
    return renderAccessSelect(feature, options);
  };

  const yakiColumns = [
    {
      header: 'Applies to',
      width: 320,
      render: (r) => (
        <AppliesToCell>
          {r.type === 'NAV' ? 'All Yaki features (master toggle)' : appliesToLabel(r)}
        </AppliesToCell>
      ),
    },
    {
      header: 'Type',
      width: 100,
      muted: true,
      render: (r) => <Tag $tone="gray">{r.type === 'NAV' ? 'Section' : 'Action'}</Tag>,
    },
    {
      header: 'Access',
      width: 160,
      render: (r) => renderYakiAccessSelect(r),
    },
  ];

  const actionBulkActions = (
    <>
      {bulkSaving && <CircularProgress size={18} />}
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(1, 'allow')}
      >
        Set allow
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer' }}
        onClick={() => !bulkSaving && handleBulkApply(2, 'read-only')}
      >
        Set read-only
      </Chip>
      <Chip
        $soft
        style={{ cursor: bulkSaving ? 'not-allowed' : 'pointer', color: '#d6455d' }}
        onClick={() => !bulkSaving && handleBulkApply(0, 'disabled')}
      >
        Set disabled
      </Chip>
      <Chip $soft style={{ cursor: 'pointer' }} onClick={clearSelection}>
        Clear selection
      </Chip>
    </>
  );

  const navColumns = [
    {
      header: 'Category',
      key: 'category',
      muted: true,
      width: 200,
    },
    {
      header: 'Access',
      width: 160,
      render: (r) => renderAccessSelect(r, NAV_OPTIONS),
    },
  ];

  const actionColumns = [
    {
      header: 'Applies to',
      width: 320,
      render: (r) => <AppliesToCell>{appliesToLabel(r)}</AppliesToCell>,
    },
    {
      header: 'Section',
      key: 'section',
      muted: true,
      width: 130,
    },
    {
      header: 'Group',
      width: 130,
      muted: true,
      render: (r) => (
        <Tag $tone="gray">{inferActionSubgroup(r.featureKey)}</Tag>
      ),
    },
    {
      header: 'Access',
      width: 160,
      render: (r) => renderAccessSelect(r, ACTION_OPTIONS),
    },
  ];

  const renderSectionFilter = (id, value, onChange, options) => (
    <SectionFilterWrap>
      <label htmlFor={id}>Section</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="all">All sections</option>
        {options.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.label}
          </option>
        ))}
      </select>
    </SectionFilterWrap>
  );

  const renderNavTable = (rows, tableKey, onSelectionChange, showBulkBar) => (
    <DataTable
      key={tableKey}
      rows={rows}
      rowKey={(r) => r.featureKey}
      loading={loading}
      selectable
      onSelectionChange={onSelectionChange}
      bulkActions={showBulkBar ? navBulkActions : null}
      pageSize={PAGE_SIZE}
      search={false}
      primary={{
        header: 'Feature name',
        label: (r) => r.label,
        sub: (r) => r.featureKey,
        short: (r) => (r.label || '?').charAt(0).toUpperCase(),
      }}
      columns={[
        {
          header: 'Sidebar label',
          width: 180,
          render: (r) => r.label,
        },
        ...navColumns,
      ]}
      emptyText="No navigation features match your filters."
    />
  );

  const renderActionTable = (rows, tableKey, onSelectionChange, showBulkBar) => (
    <DataTable
      key={tableKey}
      rows={rows}
      rowKey={(r) => r.featureKey}
      loading={loading}
      selectable
      onSelectionChange={onSelectionChange}
      bulkActions={showBulkBar ? actionBulkActions : null}
      pageSize={PAGE_SIZE}
      search={false}
      primary={{
        header: 'Button / menu',
        label: (r) => r.label,
        sub: (r) => r.featureKey,
        short: (r) => (r.label || '?').charAt(0).toUpperCase(),
      }}
      columns={actionColumns}
      emptyText="No actions match your filters."
    />
  );

  const renderYakiTable = (rows, tableKey, onSelectionChange, showBulkBar) => (
    <DataTable
      key={tableKey}
      rows={rows}
      rowKey={(r) => r.featureKey}
      loading={loading}
      selectable
      onSelectionChange={onSelectionChange}
      bulkActions={showBulkBar ? yakiBulkActions : null}
      pageSize={PAGE_SIZE}
      search={false}
      primary={{
        header: 'Feature',
        label: (r) => r.label,
        sub: (r) => r.featureKey,
        short: (r) => (r.label || '?').charAt(0).toUpperCase(),
      }}
      columns={yakiColumns}
      emptyText="No Yaki features match your filters."
    />
  );

  const navFlatMode = navSearch.trim().length > 0 || navSectionFilter !== 'all';
  const actionFlatMode = actionSearch.trim().length > 0;
  const yakiFlatMode = yakiSearch.trim().length > 0;

  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <h2>Demo access</h2>
            <p>
              Control what a read-only demo account can see and do. Use the <b>Navigation</b> tab for
              sidebar items, the <b>Actions</b> tab for buttons and menus, and the <b>Yaki</b> tab for
              the chatbot and course analysis. Changes apply to every demo user within ~1 minute.
            </p>
            {!loading && !error && (
              <StatsRow>
                <span>
                  <b>{features.length}</b> features
                </span>
                <span>
                  <b>{navRows.length}</b> navigation
                </span>
                <span>
                  <b>{actionRows.length}</b> actions
                </span>
                <span>
                  <b>{yakiRows.length}</b> yaki
                </span>
              </StatsRow>
            )}
          </PageIntro>

          {loading && (
            <LoadingWrap>
              <CircularProgress size={28} />
            </LoadingWrap>
          )}

          {!loading && error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              <SetTabs style={{ marginBottom: 16 }}>
                <SetTab
                  $on={activeTab === TAB_NAV}
                  onClick={() => setActiveTab(TAB_NAV)}
                >
                  <TabLabel>
                    Navigation
                    <TabCount $on={activeTab === TAB_NAV}>{navRows.length}</TabCount>
                  </TabLabel>
                </SetTab>
                <SetTab
                  $on={activeTab === TAB_ACTION}
                  onClick={() => setActiveTab(TAB_ACTION)}
                >
                  <TabLabel>
                    Actions
                    <TabCount $on={activeTab === TAB_ACTION}>{actionRows.length}</TabCount>
                  </TabLabel>
                </SetTab>
                <SetTab
                  $on={activeTab === TAB_YAKI}
                  onClick={() => setActiveTab(TAB_YAKI)}
                >
                  <TabLabel>
                    Yaki
                    <TabCount $on={activeTab === TAB_YAKI}>{yakiRows.length}</TabCount>
                  </TabLabel>
                </SetTab>
              </SetTabs>

              {activeTab === TAB_NAV && (
                <TabPanel>
                  <TabHint>
                    Controls what appears in the left sidebar for demo users. <b>Visible</b> = normal
                    link; <b>Disabled</b> = shown greyed with a lock dialog; <b>Hidden</b> = removed
                    from the sidebar.
                  </TabHint>

                  <ToolbarRow>
                    <SearchWrap>
                      <input
                        type="search"
                        placeholder="Search by name or feature key…"
                        value={navSearch}
                        onChange={(e) => setNavSearch(e.target.value)}
                      />
                    </SearchWrap>
                    {renderSectionFilter(
                      'demo-nav-category-filter',
                      navSectionFilter,
                      setNavSectionFilter,
                      navSectionOptions,
                    )}
                  </ToolbarRow>

                  {navSelectedRows.length > 0 && (
                    <FloatingBulkBar>
                      <span>
                        <b>{navSelectedRows.length}</b> selected
                      </span>
                      <div style={{ flex: 1 }} />
                      {navBulkActions}
                    </FloatingBulkBar>
                  )}

                  {navFlatMode ? (
                    renderNavTable(
                      filteredNavRows,
                      `nav-flat-${navTableResetKey}`,
                      setNavSelectedRows,
                      true,
                    )
                  ) : (
                    navCategoryGroups.map((group) => (
                      <SectionBlock key={group.category}>
                        <StickySectionHead>{group.category}</StickySectionHead>
                        {renderNavTable(
                          group.rows,
                          `nav-${group.category}-${navTableResetKey}`,
                          (rows) => {
                            setNavSelectedRows((prev) => {
                              const keys = new Set(group.rows.map((r) => r.featureKey));
                              const without = prev.filter((r) => !keys.has(r.featureKey));
                              return [...without, ...rows];
                            });
                          },
                          false,
                        )}
                      </SectionBlock>
                    ))
                  )}
                </TabPanel>
              )}

              {activeTab === TAB_ACTION && (
                <TabPanel>
                  <TabHint>
                    Controls individual buttons and menu items across the app.{' '}
                    <b>Read-only</b> = shown but locked for demo users; <b>Disabled</b> = hidden.
                    Use the <b>Applies to</b> column to see which UI element each row controls.
                  </TabHint>

                  <ToolbarRow>
                    <SearchWrap>
                      <input
                        type="search"
                        placeholder="Search label, key, or UI location…"
                        value={actionSearch}
                        onChange={(e) => setActionSearch(e.target.value)}
                      />
                    </SearchWrap>
                    {renderSectionFilter(
                      'demo-action-section-filter',
                      actionSectionFilter,
                      setActionSectionFilter,
                      actionSectionOptions,
                    )}
                  </ToolbarRow>

                  {actionSelectedRows.length > 0 && (
                    <FloatingBulkBar>
                      <span>
                        <b>{actionSelectedRows.length}</b> selected
                      </span>
                      <div style={{ flex: 1 }} />
                      {actionBulkActions}
                    </FloatingBulkBar>
                  )}

                  {actionFlatMode ? (
                    renderActionTable(
                      filteredActionRows,
                      `action-flat-${actionTableResetKey}`,
                      setActionSelectedRows,
                      false,
                    )
                  ) : (
                    actionSectionGroups.map((section) => (
                      <SectionBlock key={section.key}>
                        <StickySectionHead>{section.label}</StickySectionHead>
                        {section.subgroups.map((sub) => (
                          <SubgroupBlock key={`${section.key}-${sub.key}`}>
                            <SubgroupHead>{sub.label}</SubgroupHead>
                            {renderActionTable(
                              sub.rows,
                              `action-${section.key}-${sub.key}-${actionTableResetKey}`,
                              (rows) =>
                                handleActionSelectionChange(section.key, sub.key, rows),
                              false,
                            )}
                          </SubgroupBlock>
                        ))}
                      </SectionBlock>
                    ))
                  )}
                </TabPanel>
              )}

              {activeTab === TAB_YAKI && (
                <TabPanel>
                  <TabHint>
                    Controls the floating <b>Yaki assistant</b> widget and <b>course analysis</b> on
                    course detail pages. The master <b>Yaki features</b> row blocks all Yaki API routes
                    when hidden or disabled. Individual rows fine-tune the chatbot vs analysis actions.
                  </TabHint>

                  <ToolbarRow>
                    <SearchWrap>
                      <input
                        type="search"
                        placeholder="Search label, key, or UI location…"
                        value={yakiSearch}
                        onChange={(e) => setYakiSearch(e.target.value)}
                      />
                    </SearchWrap>
                  </ToolbarRow>

                  {yakiSelectedRows.length > 0 && (
                    <FloatingBulkBar>
                      <span>
                        <b>{yakiSelectedRows.length}</b> selected
                      </span>
                      <div style={{ flex: 1 }} />
                      {yakiBulkActions}
                    </FloatingBulkBar>
                  )}

                  {yakiFlatMode ? (
                    renderYakiTable(
                      filteredYakiRows,
                      `yaki-flat-${yakiTableResetKey}`,
                      setYakiSelectedRows,
                      true,
                    )
                  ) : (
                    <>
                      {filteredYakiNavRows.length > 0 && (
                        <SectionBlock>
                          <StickySectionHead>Master toggle</StickySectionHead>
                          {renderYakiTable(
                            filteredYakiNavRows,
                            `yaki-nav-${yakiTableResetKey}`,
                            (rows) => {
                              setYakiSelectedRows((prev) => {
                                const keys = new Set(filteredYakiNavRows.map((r) => r.featureKey));
                                const without = prev.filter((r) => !keys.has(r.featureKey));
                                return [...without, ...rows];
                              });
                            },
                            false,
                          )}
                        </SectionBlock>
                      )}
                      {filteredYakiActionRows.length > 0 && (
                        <SectionBlock>
                          <StickySectionHead>Yaki actions</StickySectionHead>
                          {renderYakiTable(
                            filteredYakiActionRows,
                            `yaki-actions-${yakiTableResetKey}`,
                            (rows) => {
                              setYakiSelectedRows((prev) => {
                                const keys = new Set(filteredYakiActionRows.map((r) => r.featureKey));
                                const without = prev.filter((r) => !keys.has(r.featureKey));
                                return [...without, ...rows];
                              });
                            },
                            false,
                          )}
                        </SectionBlock>
                      )}
                    </>
                  )}
                </TabPanel>
              )}
            </>
          )}
        </Inner>
      </Shell>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DemoAccessSettings;

const Shell = styled.div`
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const Inner = styled.div`
  width: min(1180px, 95%);
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin: 4px 0 22px;

  h2 {
    margin: 0 0 6px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fsSm};
    line-height: 1.55;
    color: ${({ theme }) => theme.textMuted};
    max-width: 72ch;
  }
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};

  b {
    color: ${({ theme }) => theme.text};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5em;
  padding: 0 7px;
  height: 20px;
  border-radius: ${({ theme }) => theme.rPill};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $on, theme }) => ($on ? theme.accentStrong : theme.textMuted)};
  background: ${({ $on, theme }) =>
    $on
      ? `color-mix(in oklch, ${theme.accent} 14%, ${theme.surface})`
      : theme.surface3};
  box-shadow: inset 0 0 0 1px
    ${({ $on, theme }) =>
      $on
        ? `color-mix(in oklch, ${theme.accent} 22%, transparent)`
        : `color-mix(in oklch, ${theme.border} 80%, transparent)`};
`;

const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabHint = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 220px;

  input {
    width: 100%;
    height: 36px;
    padding: 0 12px;
    border-radius: ${({ theme }) => theme.rMd};
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.accent};
    }
  }
`;

const SectionFilterWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 650;
    color: ${({ theme }) => theme.textMuted};
    white-space: nowrap;
  }

  select {
    height: 36px;
    min-width: 180px;
    padding: 0 10px;
    border-radius: ${({ theme }) => theme.rMd};
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    font-family: inherit;
    cursor: pointer;

    &:hover {
      border-color: ${({ theme }) => theme.borderStrong};
    }

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.accent};
    }
  }
`;

const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StickySectionHead = styled.h3`
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 8px 0 0;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  font-family: ${({ theme }) => theme.fontHead};
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textMuted};
`;

const SubgroupBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 4px;
`;

const SubgroupHead = styled.h4`
  margin: 4px 0 0 10px;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.textMuted};
`;

const FloatingBulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};

  b {
    color: ${({ theme }) => theme.text};
    font-variant-numeric: tabular-nums;
  }
`;

const AppliesToCell = styled.span`
  font-size: ${({ theme }) => theme.fsSm};
  line-height: 1.45;
  color: ${({ theme }) => theme.text};
`;

const AccessControl = styled(FormControl)`
  min-width: 130px;

  .MuiOutlinedInput-root {
    background: ${({ theme }) => theme.surface};
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 600;
    border-radius: ${({ theme }) => theme.rSm};

    fieldset {
      border-color: ${({ theme }) => theme.borderStrong};
    }

    &:hover fieldset {
      border-color: ${({ theme }) =>
        `color-mix(in oklch, ${theme.accent} 35%, ${theme.borderStrong})`};
    }

    &.Mui-focused fieldset {
      border-color: ${({ theme }) => theme.accent};
    }
  }
`;
